import {
  administrability,
  type AdministrableTarget
} from '../../../service/userLifecycle.service';

export type AccessLevel = 'organisation' | 'entity' | 'workspace';

export interface AccessFilters {
  role?: string;
  status?: 'active' | 'suspended' | 'invited';
  nodeId?: number;
  search?: string;
}

/** True when any filter (including the search box) narrows the list. */
export const hasActiveFilters = (filters: AccessFilters): boolean =>
  Boolean(filters.role || filters.status || filters.nodeId || (filters.search ?? '').trim());

/** What the table does with a row; the server still decides each request. */
export interface RowActionRules {
  /** Change role / open the member drawer. */
  manage: boolean;
  remove: boolean;
  /** Suspend and reactivate the account: only where accounts are administered. */
  suspend: boolean;
  /** Reset the sign-in factors: same gate as suspend. */
  resetFactors: boolean;
}

/**
 * Which row actions the viewer sees. Workspace admins may change the role of a member below
 * them and remove them from the workspace; suspending and resetting factors stay at
 * organisation or entity level. `administrable` is whether the viewer may act on this
 * person at all (their rank is above the person's).
 */
export const rowActionRules = ({ level, canManageAccess, administrable }: {
  level: AccessLevel;
  /** Org/entity: may administer accounts here. Workspace: MEMBERSHIP manage. */
  canManageAccess: boolean;
  administrable: boolean;
}): RowActionRules => {
  const allowed = canManageAccess && administrable;
  const accountLevel = level !== 'workspace';
  return {
    manage: allowed,
    remove: allowed,
    suspend: allowed && accountLevel,
    resetFactors: allowed && accountLevel
  };
};

/**
 * Whether a workspace admin may act on a member: the row is not inherited from above, the
 * viewer holds MEMBERSHIP manage, and (the same tier rule as organisation and entity level)
 * the person is strictly below the viewer, never the viewer's own row.
 */
export const workspaceAdministrable = ({ inherited, capabilityHeld, callerRole, callerEmail, target }: {
  inherited: boolean;
  capabilityHeld: boolean;
  callerRole?: string | null;
  callerEmail?: string | null;
  target: AdministrableTarget;
}): boolean => !inherited && administrability({
  capabilityHeld,
  callerRole,
  callerEmail,
  target
}).allowed;

export interface FilterableRow {
  enabled?: boolean;
  roles?: unknown[];
  memberships?: { role?: string | null }[];
}

/**
 * The workspace member list is not filtered by the server, so its role and status filters
 * run on the loaded rows.
 */
export const filterRows = <T extends FilterableRow>(rows: T[], filters: AccessFilters): T[] =>
  rows.filter((row) => {
    if (filters.status === 'active' && row.enabled === false) return false;
    if (filters.status === 'suspended' && row.enabled !== false) return false;
    if (filters.role) {
      const held = (row.roles ?? []).map(String);
      const names = held.concat((row.memberships ?? []).map((m) => m.role ?? ''));
      if (!names.includes(filters.role)) return false;
    }
    return true;
  });
